import React from "react";

// ─────────────────────────────────────────────
// Tanikkai — Signature Security Scan Spinner
// ─────────────────────────────────────────────
const TanikkaiSpinner = ({ size = 48 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    style={{ display: "block" }}
  >
    {/* ── Outer track ring ── */}
    <circle cx="24" cy="24" r="21" stroke="#E2E8F0" strokeWidth="1.5" />

    {/* ── Rotating sweep arc ── */}
    <circle
      cx="24" cy="24" r="21"
      stroke="url(#tspinner-sweep)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="30 102"
      style={{ animation: "tspin 1.1s linear infinite", transformOrigin: "24px 24px" }}
    />

    {/* ── Inner track ring ── */}
    <circle cx="24" cy="24" r="13" stroke="#EFF6FF" strokeWidth="1" />

    {/* ── Rotating inner arc (counter) ── */}
    <circle
      cx="24" cy="24" r="13"
      strokeWidth="1"
      strokeLinecap="round"
      strokeDasharray="14 68"
      style={{ stroke: "var(--app-primary-color, #26256b)", opacity: 0.5, animation: "tspin 1.8s linear infinite reverse", transformOrigin: "24px 24px" }}
    />

    {/* ── Shield body ── */}
    <path
      d="M24 11 L33 15 L33 22 C33 27.5 28.5 32.5 24 34.5 C19.5 32.5 15 27.5 15 22 L15 15 Z"
      fill="url(#tspinner-shield)"
      opacity="0.12"
    />
    <path
      d="M24 11 L33 15 L33 22 C33 27.5 28.5 32.5 24 34.5 C19.5 32.5 15 27.5 15 22 L15 15 Z"
      strokeWidth="1.25"
      strokeLinejoin="round"
      fill="none"
      style={{ stroke: "var(--app-primary-color, #26256b)" }}
    />

    {/* ── Scan line ── */}
    <line
      x1="18" y1="22" x2="30" y2="22"
      strokeWidth="1"
      strokeLinecap="round"
      style={{ stroke: "var(--app-primary-color, #26256b)", animation: "tscan 1.1s ease-in-out infinite" }}
    />

    {/* ── Tick mark ── */}
    <path
      d="M21 22.5 L23 24.5 L27 20.5"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ stroke: "var(--app-primary-color, #26256b)" }}
    />

    {/* ── Gradient defs ── */}
    <defs>
      <linearGradient id="tspinner-sweep" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"   style={{ stopColor: "var(--app-primary-color, #26256b)", stopOpacity: 0 }} />
        <stop offset="60%"  style={{ stopColor: "var(--app-primary-color, #26256b)", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "var(--app-primary-color, #26256b)", stopOpacity: 0.4 }} />
      </linearGradient>
      <linearGradient id="tspinner-shield" x1="24" y1="11" x2="24" y2="35" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   style={{ stopColor: "var(--app-primary-color, #26256b)" }} />
        <stop offset="100%" style={{ stopColor: "var(--app-primary-color, #26256b)" }} />
      </linearGradient>
    </defs>

    <style>{`
      @keyframes tspin  { to { transform: rotate(360deg); } }
      @keyframes tscan  {
        0%,100% { transform: translateY(-3px); opacity: 0.3; }
        50%      { transform: translateY(3px);  opacity: 1;   }
      }
    `}</style>
  </svg>
);

// ─────────────────────────────────────────────
// Skeleton shimmer line
// ─────────────────────────────────────────────
const SkeletonLine = ({ width = "100%", height = 12 }) => (
  <div
    className="rounded"
    style={{
      width,
      height,
      background: "linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)",
      backgroundSize: "200% 100%",
      animation: "apiloader-shimmer 1.4s ease-in-out infinite",
    }}
  />
);

// ─────────────────────────────────────────────
// ApiLoader
// ─────────────────────────────────────────────
/**
 * ApiLoader — Tanikkai signature loader for API call states.
 *
 * variant:
 *   "spinner"  — centered security-scan spinner + label   (default)
 *   "overlay"  — frosted overlay on a relative container
 *   "bar"      — slim shimmer bar at top of a section
 *   "skeleton" — shimmer placeholder rows
 *
 * Props:
 *   variant   {"spinner"|"overlay"|"bar"|"skeleton"}
 *   label     {string}  — caption below the spinner
 *   rows      {number}  — skeleton row count
 *   size      {number}  — spinner svg size in px
 *   className {string}  — extra wrapper classes
 */
const ApiLoader = ({
  variant = "spinner",
  label = "Loading...",
  rows = 4,
  size = 48,
  className = "",
}) => {

  // ── BAR ──────────────────────────────────
  if (variant === "bar") {
    return (
      <>
        <div
          className={`w-full h-0.5 overflow-hidden ${className}`}
          style={{ background: "color-mix(in srgb, var(--app-primary-color, #26256b) 10%, transparent)" }}
        >
          <div
            className="h-full"
            style={{
              // app theme colour (Platform Settings)
              background: "linear-gradient(90deg, transparent, var(--app-primary-color, #26256b), color-mix(in srgb, var(--app-primary-color, #26256b) 45%, white), var(--app-primary-color, #26256b), transparent)",
              backgroundSize: "300% 100%",
              animation: "apiloader-bar 1.6s ease-in-out infinite",
            }}
          />
        </div>
        <style>{`
          @keyframes apiloader-bar {
            0%   { background-position: 100% 0; }
            100% { background-position: -100% 0; }
          }
        `}</style>
      </>
    );
  }

  // ── SKELETON ─────────────────────────────
  if (variant === "skeleton") {
    return (
      <>
        <div className={`space-y-3 w-full ${className}`}>
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <SkeletonLine width={i % 3 === 2 ? "55%" : "100%"} height={12} />
              {i % 2 === 0 && <SkeletonLine width="75%" height={10} />}
            </div>
          ))}
        </div>
        <style>{`
          @keyframes apiloader-shimmer {
            0%   { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </>
    );
  }

  // ── OVERLAY ──────────────────────────────
  if (variant === "overlay") {
    return (
      <div
        className={`absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-xl ${className}`}
        style={{ background: "rgba(255,255,255,0.82)", backdropFilter: "blur(4px)" }}
      >
        <TanikkaiSpinner size={size} />
        {label && (
          <p className="text-xs font-medium text-gray-500">{label}</p>
        )}
      </div>
    );
  }

  // ── SPINNER (default) ─────────────────────
  return (
    <div className={`flex flex-col items-center justify-center gap-3 py-8 w-full ${className}`}>
      <TanikkaiSpinner size={size} />
      {label && (
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500">{label}</p>
      )}
    </div>
  );
};

export default ApiLoader;


